import React, { useCallback, useEffect, useState } from 'react';
import { FieldWrapper } from './FieldWrapper';
import { TypeaheadInput } from './SelectTypeaheadField';
import { Icon } from '../Icon';
import { parseRenderOptions } from './utils';
import { is, isImmutable, List, Map } from 'immutable';
import classNames from 'classnames';
import { CodeEditor } from '../code_editor/CodeEditor';

const newListRecord = Map({ key: '', value: '' });

// Converts the value Map to a List and appends an empty row to the end.
const reduceMapToList = value =>
  value
    ? value.reduceRight(
        (values, value, key) => values.unshift(Map({ key, value })),
        List([newListRecord]),
      )
    : List([newListRecord]);

// Converts a List to a Map by reducing from the right so that for any
// duplicate keys, the first value gets used. Rows with empty keys are ignored.
const reduceListToMap = rows =>
  rows
    .filter(
      (row, index, list) =>
        !!row.get('key') &&
        !list.take(index).some(r => r.get('key') === row.get('key')),
    )
    .reduce((vals, row) => vals.set(row.get('key'), row.get('value')), Map());

// Validates a row and returns an error message if the key is empty or if the
// key has been used previously in the list.
const validateRow = (row, index, values) =>
  !row.get('key')
    ? 'Key cannot be empty. This row will be ignored.'
    : values.take(index).some(r => r.get('key') === row.get('key'))
      ? 'Key cannot be a duplicate. This row will be ignored.'
      : null;

export const MapField = props => {
  const { onChange: onChangeOrig, value } = props;

  const renderOptions = parseRenderOptions(props);

  // Use a typeahead field for the key if options are provided, or if the
  // typeahead render attribute is true
  const useTypeahead =
    (!!props.options && !props.options.isEmpty()) ||
    !!renderOptions.custom.typeahead;
  // Define the available options as those that have not yet been selected
  const availableOptions =
    useTypeahead &&
    props.options.filter(option => !value.has(option.get('value')));

  // Use a code editor for the value if the code renderAttribute is an object
  const useCodeEditor = isImmutable(renderOptions.custom.code);
  // Use a secret field for the value if the secret renderAttribute is provided.
  // It should be an array which is the path to this field from the bindings.
  const useSecretField = !!renderOptions.custom.secret;

  // Convert the map value to a list so we can show errors for duplicate keys
  // instead of overwriting data. Adds an empty record to the end of the list.
  const [values, setValues] = useState(reduceMapToList(value));

  // Track how many rows are persisted on the server, and decrease the count if
  // any rows with an index lower than this count get deleted. This value is
  // used to disable the existing key fields for secret fields
  const [persistedCount, setPersistedCount] = useState(value.size);

  // Check if the value changed outside of this component, and if it has,
  // update the local list state to match the new value
  useEffect(() => {
    if (!is(Map(value), reduceListToMap(values))) {
      setValues(reduceMapToList(value));
    }
  }, [value, values]);

  // Change event for the key of a row that updates the values state and calls
  // the original onChange function with the new field value
  const onChangeKey = useCallback(
    index => key => {
      let newValues = values.setIn([index, 'key'], key);
      if (!!newValues.last().get('key')) {
        newValues = newValues.push(newListRecord);
      }
      setValues(newValues);
      onChangeOrig(reduceListToMap(newValues));
    },
    [onChangeOrig, values],
  );

  // Change event for the value of a row that updates the values state and calls
  // the original onChange function with the new field value
  const onChangeValue = useCallback(
    index => value => {
      const newValues = values.setIn([index, 'value'], value);
      setValues(newValues);
      onChangeOrig(reduceListToMap(newValues));
    },
    [onChangeOrig, values],
  );

  // Change event for deleting a row that updates the values state and calls
  // the original onChange function with the new field value
  const onDelete = useCallback(
    index => () => {
      const newValues = values.delete(index);
      setValues(newValues);
      onChangeOrig(reduceListToMap(newValues));
      if (index < persistedCount) {
        setPersistedCount(count => count - 1);
      }
    },
    [onChangeOrig, values, persistedCount],
  );

  return (
    <FieldWrapper {...props} renderOptions={renderOptions} fieldset>
      {values.map((row, index) => {
        // Is this the last row, which represents adding a new value
        const isLast = index === values.size - 1;
        // Flag for whether the last row should be rendered, which is true if
        // the field is enabled, or the field is disabled but only has one row
        const renderLast = props.enabled || values.size === 1;
        // Check if this row has an error
        const error = !isLast ? validateRow(row, index, values) : null;
        // Does the key for this secret exist in the bindings object
        const isSecretKeyPersisted =
          useSecretField &&
          !props.bindings?.get('isClone') &&
          props.bindings?.hasIn([
            ...renderOptions.custom.secret,
            row.get('key'),
          ]);

        // Render a row for each value
        return (
          // Only render the last row if applicable per the above flag
          (!isLast || renderLast) && (
            <React.Fragment key={`row-${index}`}>
              <div
                className={classNames('input-group input-group--map-field', {
                  'input-group-sm': renderOptions.size === 'sm',
                })}
              >
                {useTypeahead ? (
                  <TypeaheadInput
                    componentOptions={{
                      size: renderOptions.size,
                      fieldClassName: renderOptions.fieldClassName,
                    }}
                    id={isLast ? props.id : `${props.id}-${index}-key`}
                    disabled={!props.enabled}
                    onChange={onChangeKey(index)}
                    onFocus={props.onFocus}
                    value={row.get('key')}
                    options={availableOptions}
                    allowNew={renderOptions.custom.allowNew}
                    placeholder={renderOptions.placeholder}
                    form={props.form}
                    invalid={renderOptions.hasErrors}
                  />
                ) : (
                  <input
                    ref={isLast ? props.focusRef : undefined}
                    className={classNames(
                      'form-control',
                      renderOptions.fieldClassName,
                      {
                        'form-control-sm': renderOptions.size === 'sm',
                        'is-invalid':
                          !!error || (isLast && renderOptions.hasErrors),
                      },
                    )}
                    type="text"
                    id={isLast ? props.id : `${props.id}-${index}-key`}
                    name={`${props.name}-${index}-key`}
                    disabled={
                      !props.enabled ||
                      (isSecretKeyPersisted && index < persistedCount)
                    }
                    onChange={e => onChangeKey(index)(e.target.value)}
                    onFocus={props.onFocus}
                    value={row.get('key')}
                    placeholder={renderOptions.placeholder}
                    form={props.form}
                  />
                )}
                {useCodeEditor ? (
                  <CodeEditor
                    simple
                    title={`${props.label}${
                      !!props.label && !!row.get('key') ? ': ' : ''
                    }${row.get('key')}`}
                    language={renderOptions.custom.code.get('language')}
                    className={renderOptions.fieldClassName}
                    wrapperClassName="code-editor--max-sm"
                    id={`${props.id}-${index}-value`}
                    onChange={onChangeValue(index)}
                    value={row.get('value')}
                    bindings={
                      isImmutable(renderOptions.custom.code.get('options'))
                        ? renderOptions.custom.code.get('options').toJS()
                        : renderOptions.custom.code.get('options')
                    }
                    placeholder={renderOptions.custom.valuePlaceholder}
                    disabled={!props.enabled}
                    allowCopy={renderOptions.custom.code.get('allowCopy')}
                  />
                ) : useSecretField ? (
                  <>
                    <input
                      className={classNames(
                        'form-control',
                        renderOptions.fieldClassName,
                        { 'form-control-sm': renderOptions.size === 'sm' },
                      )}
                      type="password"
                      id={`${props.id}-${index}-value`}
                      name={`${props.name}-${index}-value`}
                      disabled={
                        (row.get('value') === null && isSecretKeyPersisted) ||
                        !props.enabled
                      }
                      onChange={e => onChangeValue(index)(e.target.value)}
                      value={row.get('value') === null ? '' : row.get('value')}
                      placeholder={
                        row.get('value') === null && isSecretKeyPersisted
                          ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022'
                          : renderOptions.custom.valuePlaceholder
                      }
                      form={props.form}
                      key={
                        row.get('value') === null
                          ? 'secret-placeholder'
                          : 'secret-field'
                      }
                      autoFocus={
                        row.get('value') !== null && isSecretKeyPersisted
                      }
                    />
                    {isSecretKeyPersisted &&
                      index < persistedCount &&
                      (row.get('value') === null ? (
                        <button
                          className="btn btn-outline-primary"
                          type="button"
                          onClick={() => onChangeValue(index)('')}
                        >
                          Modify
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-primary"
                          type="button"
                          onClick={() => onChangeValue(index)(null)}
                        >
                          Cancel
                        </button>
                      ))}
                  </>
                ) : (
                  <input
                    className={classNames(
                      'form-control',
                      renderOptions.fieldClassName,
                      { 'form-control-sm': renderOptions.size === 'sm' },
                    )}
                    type="text"
                    id={`${props.id}-${index}-value`}
                    name={`${props.name}-${index}-value`}
                    disabled={!props.enabled}
                    onChange={e => onChangeValue(index)(e.target.value)}
                    value={row.get('value')}
                    placeholder={renderOptions.custom.valuePlaceholder}
                    form={props.form}
                  />
                )}
                {props.enabled && !isLast && (
                  <button
                    className="btn btn-table"
                    type="button"
                    onClick={onDelete(index)}
                    aria-label="Delete Row"
                    title="Delete Row"
                  >
                    <Icon name="trash" md />
                  </button>
                )}
              </div>
              {error && <div className="form-text text-danger">{error}</div>}
            </React.Fragment>
          )
        );
      })}
    </FieldWrapper>
  );
};
